import {
  Alert,
  Box,
  Button,
  EditableText,
  Gap,
  Icon,
  IconButton,
  Input,
  LinkButton,
  Modal,
  SingleDatePicker,
  Span,
  useTheme,
  LookBackPeriod,
  LookBackMeasurement,
  convertToMinutes,
  convertToMeasurement,
} from '@salecycle/ui-component-library';
import { UNSUBSCRIBE_ITEMS } from 'core/constants/UnsubscribeConstants';
import {
  resetGradientBarVariation,
  updateGradientBarVariation,
  usePreferencesDispatch,
  usePreferencesState,
} from 'core/preferences/usePreferencesContext';
import useUserDetails from 'core/ui/utils/useUserDetails';
import { basisTranslationMap } from 'entities/consent';
import { Helpers } from 'entities/lifecycle';
import { getGradientVariation } from 'entities/lifecycle/activityState';
import moment, { Moment } from 'moment-timezone';
import React, { useMemo } from 'react';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router';
import { useHistory, useParams } from 'react-router-dom';
import { UnsubscribeSettings } from 'sections/client-management/entities/site';
import { OffsiteCampaignRoute } from 'sections/offsite-campaigns/routes/offsite-campaign-routes';
import { StateBadge } from '../../../components/StateBadge';
import { IOffsiteCampaign, IPercentageSplit, SuppressTimeWindow } from '../../../entities/offsiteCampaign';
import {
  useOffsiteCampaignEditorDispatch,
  useOffsiteCampaignEditorState,
  useDoesSplitTestExist,
  useHasSplitTestEnded,
} from '../../context/UseOffsiteCampaignEditorContext';
import { CampaignActionsButton } from './CampaignActionsButton';
import {
  HeaderWrapper,
  LastEdited,
  LinkIcon,
  SidebarDates,
  SidebarHeaderContainer,
  SidebarLabel,
  SidebarList,
  SidebarListItem,
  StyledSidebar,
  TimezoneOffset,
} from './styles';
import { FeatureTypes } from 'entities/campaign/features';
import { useFeatureFlags } from 'core/app/AppContext';
import { UpdatedSplitTestPercentages } from '../common/PercentageCalculator';
import { SiteEnrichmentList } from '../../../../client-management/ui/pages/client-settings/integrations/utils/enrichmentOptions';

type ICampaignTimeType =
  | 'startTime'
  | 'endTime'
  | 'splitTestStartTime'
  | 'splitTestEndTime'
  | 'supressionStartTime'
  | 'supressionEndTime';

function changeInLifecycle(a: IOffsiteCampaign, b: IOffsiteCampaign): boolean {
  const result = a && b && Helpers.getActivityState(a) !== Helpers.getActivityState(b);
  return result;
}

export const Sidebar: FC = () => {
  const dispatch = useOffsiteCampaignEditorDispatch();
  const { t } = useTranslation(['integrations', 'campaigns', 'marketingPermissions', 'common']);
  const { siteId, campaignId } = useParams<{ siteId: string; campaignId: string }>();
  const {
    hasUnsavedCampaignChange,
    unsavedSegmentChanges,
    hasMissingFields,
    campaign,
    originalCampaign,
    site,
  } = useOffsiteCampaignEditorState();
  const theme = useTheme();
  const { timezone } = usePreferencesState();
  const preferencesDispatch = usePreferencesDispatch();
  const gradientVariation = getGradientVariation(campaign);
  const {
    push,
    location: { pathname },
  } = useHistory();
  const baseUrl = pathname.replace('/campaigns/offsite/' + campaignId, '');
  const { flagEnabled } = useFeatureFlags();

  const doesSplitTestExist = useDoesSplitTestExist();
  const [isClosePromptOpen, setIsClosePromptOpen] = useState(false);
  const [isStartDateFocused, setIsStartDateFocused] = useState(false);
  const [isEndDateFocused, setIsEndDateFocused] = useState(false);
  const [isSplitTestStartDateFocused, setIsSplitTestStartDateFocused] = useState(false);
  const [isSplitTestEndDateFocused, setIsSplitTestEndDateFocused] = useState(false);
  const [addSuppressionTime, setAddSuppressionTime] = useState(
    campaign.suppressTimeWindows.length === 0 ? false : true,
  );
  const [minimumBrowseTime, setMinimumBrowseTime] = useState(campaign.minimumBrowseTime);
  const isSplitTestFinished = useHasSplitTestEnded();
  const featureSplitTest = flagEnabled('ld-split-test-scheduling');
  const defaultLookBackPeriod = useMemo(() => convertToMeasurement(campaign.lookbackPeriod), [campaign.lookbackPeriod]);
  const enrichments = SiteEnrichmentList(siteId);
  const hasTrendsEnrichment = enrichments?.some(enrich => enrich.value === 'Trends');
  const lookbackFlagEnabled = flagEnabled('ld-trends');
  React.useEffect(() => {
    setAddSuppressionTime(campaign.suppressTimeWindows.length === 0 ? false : true);
  }, [campaign.suppressTimeWindows]);

  React.useEffect(() => {
    setMinimumBrowseTime(campaign.minimumBrowseTime);
  }, [campaign.minimumBrowseTime]);

  // Get the user details for the Last Edited by text.
  const { userDetails, status: userDetailsStatus } = useUserDetails(campaign.updatedById);

  useEffect(() => {
    updateGradientBarVariation(preferencesDispatch, gradientVariation);

    return () => resetGradientBarVariation(preferencesDispatch);
  }, [preferencesDispatch, gradientVariation, campaign.state]);

  if (!campaign) {
    return null;
  }

  const timezoneOffset = `UTC ${moment()
    .tz(timezone)
    .format('Z')
    .replace(':00', '')}`;

  const startTimeMoment = campaign.startTime ? moment(campaign.startTime).tz(timezone) : null;
  const endTimeMoment = campaign.endTime ? moment(campaign.endTime).tz(timezone) : null;
  const splitTestStartTimeMoment = campaign.splitTestStartDate
    ? moment(campaign.splitTestStartDate).tz(timezone)
    : null;
  const splitTestEndTimeMoment = campaign.splitTestEndDate ? moment(campaign.splitTestEndDate).tz(timezone) : null;
  const nowMoment = moment().tz(timezone);
  const sameDayEnd = startTimeMoment && endTimeMoment && startTimeMoment.isSame(endTimeMoment, 'date');
  const endToday = nowMoment && endTimeMoment && nowMoment.isSame(endTimeMoment, 'date');
  const sameDaySplitTestEnd =
    splitTestStartTimeMoment &&
    splitTestEndTimeMoment &&
    splitTestStartTimeMoment.isSame(splitTestEndTimeMoment, 'date');
  const endTodaySplitTest = nowMoment && splitTestEndTimeMoment && nowMoment.isSame(splitTestEndTimeMoment, 'date');
  const suppressionStartTimeMoment = campaign.suppressTimeWindows[0]?.suppressTimeStart
    ? moment(campaign.suppressTimeWindows[0].suppressTimeStart).tz(timezone)
    : moment()
        .tz(timezone)
        .startOf('day');

  const suppressionEndTimeMoment = campaign.suppressTimeWindows[0]?.suppressTimeEnd
    ? moment(campaign.suppressTimeWindows[0].suppressTimeEnd).tz(timezone)
    : moment()
        .tz(timezone)
        .startOf('day');

  const onChangeTime = (date: Moment, type: ICampaignTimeType) => {
    switch (type) {
      case 'startTime':
        const maximumStartDateTime = date ? setValidStartTime(date) : 0;
        dispatch({
          type: 'SET_CAMPAIGN_START_TIME',
          payload: { startTime: maximumStartDateTime },
        });
        break;
      case 'endTime':
        const minimumEndDateTime = date ? setValidEndTime(date) : 0;
        dispatch({
          type: 'SET_CAMPAIGN_END_TIME',
          payload: { endTime: minimumEndDateTime },
        });
        break;
      case 'splitTestStartTime':
        const maximumSplitTestStartDateTime = date ? setValidSplitTestStartTime(date) : 0;
        dispatch({
          type: 'SET_CAMPAIGN_SPLIT_TEST_START_TIME',
          payload: { splitTestStartDate: maximumSplitTestStartDateTime },
        });
        break;
      case 'splitTestEndTime':
        const minimumSplitTestEndDateTime = date ? setValidSplitTestEndTime(date) : 0;
        dispatch({
          type: 'SET_CAMPAIGN_SPLIT_TEST_END_TIME',
          payload: { splitTestEndDate: minimumSplitTestEndDateTime },
        });
        break;
    }
  };

  const onSuppressTimeChange = (date: Moment, type: ICampaignTimeType) => {
    const suppressTimeWindows = [];

    switch (type) {
      case 'supressionStartTime':
        for (let i = 1; i < 8; i++) {
          suppressTimeWindows.push({
            suppressTimeStart: date?.valueOf() ?? 0,
            suppressTimeEnd: suppressionEndTimeMoment.valueOf(),
            dayOfTheWeek: i,
          } as SuppressTimeWindow);
        }

        break;
      case 'supressionEndTime':
        for (let i = 1; i < 8; i++) {
          suppressTimeWindows.push({
            suppressTimeStart: suppressionStartTimeMoment.valueOf(),
            suppressTimeEnd: date?.valueOf(),
            dayOfTheWeek: i,
          } as SuppressTimeWindow);
        }
        break;
    }
    dispatch({
      type: 'SET_CAMPAIGN_SUPPRESSION_TIME',
      payload: { suppressTimeWindows: suppressTimeWindows },
    });
  };

  const onSupressTimeTrigger = () => {
    if (addSuppressionTime) {
      dispatch({
        type: 'REMOVE_SUPPRESSION_TIME',
        payload: {},
      });
    }
    setAddSuppressionTime(!addSuppressionTime);
  };

  const setValidEndTime = (date: Moment) => {
    if (sameDayEnd && startTimeMoment && date.isBefore(startTimeMoment)) {
      return startTimeMoment.valueOf();
    } else if (endToday && date.isBefore(nowMoment)) {
      return nowMoment.valueOf();
    }
    return date.valueOf();
  };

  const setValidStartTime = (date: Moment) => {
    if (sameDayEnd && endTimeMoment && date.isAfter(endTimeMoment)) {
      return endTimeMoment.valueOf();
    } else if (date.isBefore(nowMoment)) {
      return nowMoment.valueOf();
    }
    return date.valueOf();
  };

  const setValidSplitTestEndTime = (date: Moment) => {
    if (sameDaySplitTestEnd && splitTestStartTimeMoment && date.isBefore(splitTestStartTimeMoment)) {
      return splitTestStartTimeMoment.valueOf();
    } else if (endTodaySplitTest && date.isBefore(nowMoment)) {
      return nowMoment.valueOf();
    }
    return date.valueOf();
  };

  const setValidSplitTestStartTime = (date: Moment) => {
    if (sameDaySplitTestEnd && splitTestEndTimeMoment && date.isAfter(splitTestEndTimeMoment)) {
      return splitTestEndTimeMoment.valueOf();
    } else if (date.isBefore(nowMoment)) {
      return nowMoment.valueOf();
    }
    return date.valueOf();
  };

  const updateCampaignName = (name: string) => {
    dispatch({
      type: 'SET_CAMPAIGN_NAME',
      payload: { name },
    });
  };

  const onBackLinkClick = () => {
    if (hasUnsavedCampaignChange || unsavedSegmentChanges.length > 0) {
      setIsClosePromptOpen(true);
    } else {
      push(generatePath(OffsiteCampaignRoute.OFFSITE_LIST, { siteId }));
    }
  };

  const backToCampaigns = () => push(generatePath(OffsiteCampaignRoute.OFFSITE_LIST, { siteId }));

  const onModalSaveClick = () => {
    backToCampaigns();
  };

  const unsubscribeValue = (unsubscribeSettings: UnsubscribeSettings) => {
    if (unsubscribeSettings?.globalUnsubscribe) {
      return t(`common:${UNSUBSCRIBE_ITEMS.find(item => item.id === 'GlobalUnsubscribe').name}`);
    }
    if (unsubscribeSettings?.unsubscribeURL) {
      return unsubscribeSettings.unsubscribeURL;
    }
    if (unsubscribeSettings?.unsubscribeTemplate) {
      return t('common:client.siteForm.unsubscribe.unsubscribeTemplate.label');
    }
    return t(`common:${UNSUBSCRIBE_ITEMS.find(item => item.id === 'GlobalUnsubscribe').name}`);
  };

  const updateMinimumBrowseTime = (value: string) => {
    const numericValue = parseInt(value);
    dispatch({
      type: 'SET_MINIMUM_BROWSE_TIME',
      payload: { minimumBrowseTime: numericValue },
    });
  };

  const updateLookBackPeriod = (value: number, measurement: LookBackMeasurement) => {
    const minutes = convertToMinutes(value, measurement);
    dispatch({
      type: 'UPDATE_LOOKBACK_PERIOD',
      payload: { lookbackPeriod: minutes },
    });
  };

  const getMinimumBrowseTimeValidationMessage = (value: number): string => {
    if (Number.isNaN(value) || value > 30) {
      return t('campaigns:newCampaigns.details.minimumBrowseTime.validationWarning');
    }
  };

  const updateSplitTests = (segmentId: string, updatedSplits: IPercentageSplit[]) => {
    dispatch({
      type: 'UPDATE_SPLIT_TESTS',
      payload: {
        segmentId: segmentId,
        updatedPercentageSplits: UpdatedSplitTestPercentages(updatedSplits),
      },
    });
  };
  const deleteAllSplitTests = () => {
    const { segments } = campaign;

    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      const defaultSplit = segment.percentageSplits.length > 1 && segment.percentageSplits?.filter(s => s.isDefault);
      updateSplitTests(segment.id, defaultSplit);
    }
  };

  const hasFinished = isSplitTestFinished && featureSplitTest;

  const CampaignEditWarning = () => {
    const splitTestExists = originalCampaign.segments.some(s => s.percentageSplits.length > 1);
    if (!splitTestExists) return null;

    const editingLiveCampaign = originalCampaign.state === 'ACTIVE';
    const editingPausedCampaign = originalCampaign.state === 'INACTIVE';
    const heading = hasFinished
      ? t('campaigns:offsiteEditor.sidebar.alert.alertHeadingFinishedSplitTest')
      : t('campaigns:offsiteEditor.sidebar.alert.alertHeadingEditingCampaign');

    let alertDescription: string | React.ReactNode;
    const removeTestsUI = (
      <>
        <Button onClick={deleteAllSplitTests}>Remove all split tests</Button>
        <p>{t('campaigns:offsiteEditor.sidebar.alert.alertDescriptionFinishedSplitTest')}</p>
        <p>{t('campaigns:offsiteEditor.sidebar.alert.alertDescriptionFinishedSplitTest2')}</p>
      </>
    );
    switch (true) {
      case editingLiveCampaign:
        alertDescription = hasFinished
          ? removeTestsUI
          : t('campaigns:offsiteEditor.sidebar.alert.alertDescriptionLiveCampaign');
        break;
      case editingPausedCampaign:
        alertDescription = hasFinished
          ? removeTestsUI
          : t('campaigns:offsiteEditor.sidebar.alert.alertDescriptionPausedCampaign');
        break;
      default:
        return null;
    }

    return <Alert variant="warning" heading={heading} description={alertDescription} />;
  };

  return (
    <StyledSidebar data-testid="Sidebar">
      <Box margin="0 0 3rem 0">
        <LinkButton ariaLabel={t('campaigns:offsiteEditor.sidebar.back')} onClick={onBackLinkClick}>
          <Icon icon="angle-double-left" /> {t('campaigns:offsiteEditor.sidebar.back')}
        </LinkButton>
      </Box>

      <SidebarHeaderContainer>
        <HeaderWrapper>
          <EditableText
            onChange={updateCampaignName}
            ariaLabel={t('campaigns:offsiteEditor.sidebar.items.campaignName')}
            value={campaign.name}
            spacingBelow={false}
            inputTestId="CampaignNameInput"
          ></EditableText>
        </HeaderWrapper>
      </SidebarHeaderContainer>

      <SidebarList>
        <SidebarListItem data-testid="Objective">
          <SidebarLabel>
            <Span bold={true}>{t('campaigns:offsiteEditor.sidebar.items.objective')}:</Span>
          </SidebarLabel>
          {t('campaigns:offsiteEditor.sidebar.items.cartAbandonmentObjective')}
        </SidebarListItem>
        <SidebarListItem data-testid="Feature">
          <SidebarLabel>
            <Span bold={true}>{t('campaigns:offsiteEditor.sidebar.items.feature')}:</Span>
          </SidebarLabel>
          {FeatureTypes[campaign.type]}
        </SidebarListItem>
        <SidebarListItem data-testid="Segments">
          <SidebarLabel>
            <Span bold={true}>{t('campaigns:offsiteEditor.sidebar.items.segments')}: </Span>
          </SidebarLabel>

          {campaign.segments.length}
        </SidebarListItem>
        {site && (
          <SidebarListItem data-testid="Unsubscribe">
            <SidebarLabel>
              <Span bold={true}>{t('common:client.siteForm.unsubscribe.label')}: </Span>
            </SidebarLabel>

            <a href={`${baseUrl}/settings/settings`} target="_blank" rel="noopener noreferrer">
              {unsubscribeValue(site?.unsubscribeSettings)}
              <LinkIcon>
                <Icon icon="external-link-square" color={theme.vars.newColor.darkGS2} />
              </LinkIcon>
            </a>
          </SidebarListItem>
        )}
        {site?.marketingPermissions && (
          <SidebarListItem data-testid="MPSDetails">
            <SidebarLabel>
              <Span bold={true}>{t('campaigns:offsiteEditor.sidebar.items.mps')}: </Span>
            </SidebarLabel>

            <a href={`${baseUrl}/settings/marketing-permissions`} target="_blank" rel="noopener noreferrer">
              {t(
                `marketingPermissions:basisDescriptions.${basisTranslationMap[
                  site.marketingPermissions.consentBasis[0]
                ] ?? 'disabledBasis'}.label`,
              )}

              <LinkIcon>
                <Icon icon="external-link-square" color={theme.vars.newColor.darkGS2} />
              </LinkIcon>
            </a>
          </SidebarListItem>
        )}
        <SidebarListItem data-testid="StartDate">
          <SidebarLabel>
            <Span bold={true}>{t('campaigns:offsiteEditor.sidebar.items.startDate')}:</Span>
          </SidebarLabel>

          <SidebarDates hasDate={!!startTimeMoment}>
            <SingleDatePicker
              ariaLabel={t('campaigns:dates.start.label')}
              date={startTimeMoment}
              focused={isStartDateFocused}
              isFutureDatesOnly={true}
              isMini={true}
              onDateChange={(selectedDate: Moment) =>
                onChangeTime(moment.tz(`${selectedDate.format('YYYY-MM-DD')}T00:00`, timezone), 'startTime')
              }
              onFocusChange={({ focused }) => setIsStartDateFocused(focused)}
              placeholder={t('campaigns:dates.start.datePlaceholder')}
              spacingBelow={false}
            />

            {startTimeMoment && (
              <>
                <Input
                  ariaLabel={t('campaigns:dates.start.timeLabel')}
                  type="time"
                  spacingBelow={false}
                  value={startTimeMoment.format('HH:mm')}
                  onChange={selectedTime =>
                    onChangeTime(
                      moment.tz(`${startTimeMoment.format('YYYY-MM-DD')}T${selectedTime}`, timezone),
                      'startTime',
                    )
                  }
                  isMini={true}
                />

                <IconButton
                  ariaLabel={t('campaigns:dates.start.clear')}
                  icon="times"
                  onClick={e => {
                    e.preventDefault();
                    onChangeTime(null, 'startTime');
                  }}
                />

                <TimezoneOffset data-testid="TimezoneOffset">{timezoneOffset}</TimezoneOffset>
              </>
            )}
          </SidebarDates>
        </SidebarListItem>
        <SidebarListItem data-testid="EndDate">
          <SidebarLabel>
            <Span bold={true}>{t('campaigns:offsiteEditor.sidebar.items.endDate')}:</Span>
          </SidebarLabel>

          <SidebarDates hasDate={!!endTimeMoment}>
            <SingleDatePicker
              ariaLabel={t('campaigns:dates.end.label')}
              date={endTimeMoment}
              focused={isEndDateFocused}
              isFutureDatesOnly={true}
              isMini={true}
              onDateChange={(selectedDate: Moment) =>
                onChangeTime(moment.tz(`${selectedDate.format('YYYY-MM-DD')}T23:00`, timezone), 'endTime')
              }
              onFocusChange={({ focused }) => setIsEndDateFocused(focused)}
              placeholder={t('campaigns:dates.end.datePlaceholder')}
              spacingBelow={false}
            />

            {endTimeMoment && (
              <>
                <Input
                  ariaLabel={t('campaigns:dates.end.timeLabel')}
                  type="time"
                  spacingBelow={false}
                  value={endTimeMoment.format('HH:mm')}
                  onChange={selectedTime =>
                    onChangeTime(
                      moment.tz(`${endTimeMoment.format('YYYY-MM-DD')}T${selectedTime}`, timezone),
                      'endTime',
                    )
                  }
                  isMini={true}
                />

                <IconButton
                  ariaLabel={t('campaigns:dates.end.clear')}
                  icon="times"
                  onClick={e => {
                    e.preventDefault();
                    onChangeTime(null, 'endTime');
                  }}
                />

                <TimezoneOffset data-testid="TimezoneOffset">{timezoneOffset}</TimezoneOffset>
              </>
            )}
          </SidebarDates>
        </SidebarListItem>
        {flagEnabled('ld-split-test-scheduling') && doesSplitTestExist && (
          <>
            <SidebarListItem data-testid="SplitTestStartDate">
              <SidebarLabel>
                <Span bold={true}>{t('campaigns:offsiteEditor.sidebar.items.splitTestStartDate')}:</Span>
              </SidebarLabel>

              <SidebarDates hasDate={!!splitTestStartTimeMoment}>
                <SingleDatePicker
                  ariaLabel={t('campaigns:dates.start.label')}
                  date={splitTestStartTimeMoment}
                  focused={isSplitTestStartDateFocused}
                  isFutureDatesOnly={true}
                  isMini={true}
                  onDateChange={(selectedSplitTestDate: Moment) =>
                    onChangeTime(
                      moment.tz(`${selectedSplitTestDate.format('YYYY-MM-DD')}T00:00`, timezone),
                      'splitTestStartTime',
                    )
                  }
                  onFocusChange={({ focused }) => setIsSplitTestStartDateFocused(focused)}
                  placeholder={t('campaigns:dates.start.datePlaceholder')}
                  spacingBelow={false}
                />

                {splitTestStartTimeMoment && (
                  <>
                    <Input
                      ariaLabel={t('campaigns:dates.start.timeLabel')}
                      type="time"
                      spacingBelow={false}
                      value={splitTestStartTimeMoment.format('HH:mm')}
                      onChange={selectedSplitTestTime =>
                        onChangeTime(
                          moment.tz(
                            `${splitTestStartTimeMoment.format('YYYY-MM-DD')}T${selectedSplitTestTime}`,
                            timezone,
                          ),
                          'splitTestStartTime',
                        )
                      }
                      isMini={true}
                    />

                    <IconButton
                      ariaLabel={t('campaigns:dates.start.clear')}
                      icon="times"
                      onClick={e => {
                        e.preventDefault();
                        onChangeTime(null, 'splitTestStartTime');
                      }}
                    />

                    <TimezoneOffset data-testid="TimezoneOffset">{timezoneOffset}</TimezoneOffset>
                  </>
                )}
              </SidebarDates>
            </SidebarListItem>
            <SidebarListItem data-testid="SplitTestEndDate">
              <SidebarLabel>
                <Span bold={true}>{t('campaigns:offsiteEditor.sidebar.items.splitTestEndDate')}:</Span>
              </SidebarLabel>

              <SidebarDates hasDate={!!splitTestEndTimeMoment}>
                <SingleDatePicker
                  ariaLabel={t('campaigns:dates.end.label')}
                  date={splitTestEndTimeMoment}
                  focused={isSplitTestEndDateFocused}
                  isFutureDatesOnly={true}
                  isMini={true}
                  onDateChange={(selectedSplitTestDate: Moment) =>
                    onChangeTime(
                      moment.tz(`${selectedSplitTestDate.format('YYYY-MM-DD')}T23:00`, timezone),
                      'splitTestEndTime',
                    )
                  }
                  onFocusChange={({ focused }) => setIsSplitTestEndDateFocused(focused)}
                  placeholder={t('campaigns:dates.end.datePlaceholder')}
                  spacingBelow={false}
                />

                {splitTestEndTimeMoment && (
                  <>
                    <Input
                      ariaLabel={t('campaigns:dates.end.timeLabel')}
                      type="time"
                      spacingBelow={false}
                      value={splitTestEndTimeMoment.format('HH:mm')}
                      onChange={selectedSplitTestTime =>
                        onChangeTime(
                          moment.tz(
                            `${splitTestEndTimeMoment.format('YYYY-MM-DD')}T${selectedSplitTestTime}`,
                            timezone,
                          ),
                          'splitTestEndTime',
                        )
                      }
                      isMini={true}
                    />

                    <IconButton
                      ariaLabel={t('campaigns:dates.end.clear')}
                      icon="times"
                      onClick={e => {
                        e.preventDefault();
                        onChangeTime(null, 'splitTestEndTime');
                      }}
                    />

                    <TimezoneOffset data-testid="TimezoneOffset">{timezoneOffset}</TimezoneOffset>
                  </>
                )}
              </SidebarDates>
            </SidebarListItem>
          </>
        )}
        <SidebarListItem>
          <SidebarLabel>
            <Span bold={true}>{t('campaigns:offsiteEditor.sidebar.items.suppressionStartTime')}:</Span>
          </SidebarLabel>
          <LinkButton onClick={() => onSupressTimeTrigger()}>
            {!addSuppressionTime
              ? t('campaigns:offsiteEditor.sidebar.items.suppressionTimeAdd')
              : t('campaigns:offsiteEditor.sidebar.items.suppressionTimeRemove')}
          </LinkButton>
        </SidebarListItem>
        {addSuppressionTime && (
          <>
            <SidebarListItem data-testid="SuppressionStartTime">
              <SidebarLabel>
                <Span bold={true}>{t('campaigns:offsiteEditor.sidebar.items.suppressionStartTime')}:</Span>
              </SidebarLabel>
              <SidebarDates hasDate>
                <Input
                  ariaLabel={t('campaigns:suppressionTime.start.timeLabel')}
                  type="time"
                  spacingBelow={false}
                  value={suppressionStartTimeMoment.format('HH:mm')}
                  onChange={selectedSuppressionTime =>
                    onSuppressTimeChange(
                      moment.tz(
                        `${suppressionStartTimeMoment.format('YYYY-MM-DD')}T${selectedSuppressionTime}`,
                        timezone,
                      ),
                      'supressionStartTime',
                    )
                  }
                  isMini={true}
                />
                <IconButton
                  ariaLabel={t('campaigns:dates.start.clear')}
                  icon="times"
                  onClick={e => {
                    e.preventDefault();
                    onSuppressTimeChange(null, 'supressionStartTime');
                  }}
                />
                <TimezoneOffset data-testid="TimezoneOffset">{timezoneOffset}</TimezoneOffset>
              </SidebarDates>
            </SidebarListItem>

            <SidebarListItem data-testid="SuppressionEndTime">
              <SidebarLabel>
                <Span bold={true}>{t('campaigns:offsiteEditor.sidebar.items.suppressionEndTime')}:</Span>
              </SidebarLabel>
              <SidebarDates hasDate>
                <Input
                  ariaLabel={t('campaigns:suppressionTime.end.timeLabel')}
                  type="time"
                  spacingBelow={false}
                  value={suppressionEndTimeMoment.format('HH:mm')}
                  onChange={selectedSuppressionTime =>
                    onSuppressTimeChange(
                      moment.tz(
                        `${suppressionEndTimeMoment.format('YYYY-MM-DD')}T${selectedSuppressionTime}`,
                        timezone,
                      ),
                      'supressionEndTime',
                    )
                  }
                  isMini={true}
                />
                <IconButton
                  ariaLabel={t('campaigns:dates.start.clear')}
                  icon="times"
                  onClick={e => {
                    e.preventDefault();
                    onSuppressTimeChange(null, 'supressionEndTime');
                  }}
                />
                <TimezoneOffset data-testid="TimezoneOffset">{timezoneOffset}</TimezoneOffset>
              </SidebarDates>
            </SidebarListItem>
          </>
        )}
        {lookbackFlagEnabled && hasTrendsEnrichment && (
          <SidebarListItem data-testid="LookbackPeriod">
            <LookBackPeriod
              key={defaultLookBackPeriod.measurement}
              label={t('campaigns:offsiteEditor.sidebar.items.lookBack.label') + ':'}
              tooltipHeading={t('campaigns:offsiteEditor.sidebar.items.lookBack.tooltipTitle')}
              tooltipDescription={t('campaigns:offsiteEditor.sidebar.items.lookBack.tooltipDescription')}
              aria-label={t('campaigns:offsiteEditor.sidebar.items.ariaLabel')}
              valueAriaLabel={t('campaigns:offsiteEditor.sidebar.items.valueAriaLabel')}
              measurementAriaLabel={t('campaigns:offsiteEditor.sidebar.items.lookBack.measurementAriaLabel')}
              onChange={updateLookBackPeriod}
              defaultMeasurement={defaultLookBackPeriod.measurement}
              defaultValue={defaultLookBackPeriod.value}
              isBold={true}
            />
          </SidebarListItem>
        )}
        {campaign.type === 'browseAbandonment' && (
          <SidebarListItem data-test-id="MinimumBrowseTime">
            <SidebarLabel>
              <Span bold={true}>{t('campaigns:offsiteEditor.sidebar.items.minimumBrowseTime')}:</Span>
            </SidebarLabel>
            <Input
              key="MinimumBrowseTime"
              inputTestId="minimumBrowseTimeInputTestId"
              ariaLabel={t('campaigns:offsiteEditor.sidebar.items.minimumBrowseTime')}
              value={minimumBrowseTime}
              type="number"
              min="0"
              max="30"
              onChange={updateMinimumBrowseTime}
              isMini={true}
              validationMessage={getMinimumBrowseTimeValidationMessage(minimumBrowseTime)}
              validationState={getMinimumBrowseTimeValidationMessage(minimumBrowseTime) ? 'warning' : 'complete'}
            />
          </SidebarListItem>
        )}
        <SidebarListItem data-testid="Status">
          <SidebarLabel>
            <Span bold={true}>{t('campaigns:offsiteEditor.sidebar.items.status')}:</Span>
          </SidebarLabel>

          <Box padding="1rem 0 0">
            {changeInLifecycle(campaign, originalCampaign) ? (
              <>
                <StateBadge lifeCycle={originalCampaign} />

                <Box padding="1rem">
                  <Icon icon="angle-double-right" />
                </Box>
              </>
            ) : null}
            <StateBadge lifeCycle={campaign} />
            <Gap right={10} />
            <CampaignActionsButton />
          </Box>
        </SidebarListItem>
        <SidebarListItem data-testid="Site">
          <SidebarLabel>
            <Span bold={true}>{t('campaigns:offsiteEditor.sidebar.items.site')}: </Span>
          </SidebarLabel>
          {site.name}
        </SidebarListItem>
        <SidebarListItem>
          <CampaignEditWarning />
        </SidebarListItem>
      </SidebarList>

      {userDetailsStatus === 'resolved' && (
        <LastEdited data-testid="LastEdited">
          <Span>
            {t('campaigns:offsiteEditor.sidebar.lastEdited', {
              name: userDetails.name,
              timeago: moment(campaign.updatedAt)
                .tz(timezone)
                .fromNow(),
            })}
          </Span>
        </LastEdited>
      )}

      <Modal
        heading={t('campaigns:offsiteEditor.sidebar.close.modal.heading')}
        isOpen={isClosePromptOpen}
        variation="clean"
        okButtonLabel={t('campaigns:offsiteEditor.sidebar.close.modal.okButtonLabel')}
        onOkButtonClicked={backToCampaigns}
        cancelButtonLabel={
          hasMissingFields
            ? t('campaigns:offsiteEditor.sidebar.close.modal.cancelButtonLabel.missingFields')
            : t('campaigns:offsiteEditor.sidebar.close.modal.cancelButtonLabel.noMissingFields')
        }
        onCancelButtonClicked={hasMissingFields ? () => setIsClosePromptOpen(false) : onModalSaveClick}
        onCloseButtonClicked={() => setIsClosePromptOpen(false)}
      >
        <p>{t('campaigns:offsiteEditor.sidebar.close.modal.message')}</p>
      </Modal>
    </StyledSidebar>
  );
};
